import { useState, useEffect } from "react";
import {
    Typography,
    Card,
    CardHeader,
    CardContent,
    Grid,
    InputLabel,
    InputAdornment,
    Snackbar,
    Button,
    LinearProgress,
    TextField,
    MenuItem,
    Alert,
    AlertTitle
} from "@mui/material";
import { makeStyles } from "@mui/styles";
import { Lan, Link } from "@mui/icons-material";
import { useSnackbar } from '../providers/SnackbarProvider';
import { onTerminalFireLicenseUpdate } from "../utils/licenseBus";

const useStyles = makeStyles({
    card: {
        paddingX: "0.5rem",
        paddingY: "1rem",
        marginBottom: 20,
    }
});

// Funções de API
async function fetchConnectionInfo() {
    const res = await fetch(`${process.env.REACT_APP_URL}/terminal/tcp/connection-info`);
    if (!res.ok) throw new Error("Erro ao buscar conexão");
    return res.json();
}

async function fetchNetworkIps() {
    const res = await fetch(`${process.env.REACT_APP_URL}/terminal/tcp/network-ips`);
    if (!res.ok) throw new Error("Erro ao buscar IPs");
    return res.json();
}

async function connectAndSave(ip, port) {
    const res = await fetch(`${process.env.REACT_APP_URL}/terminal/tcp/connect-save`, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ip, port: Number(port) })
    });
    if (!res.ok) throw new Error("Erro ao conectar");
}

async function disconnect() {
    const res = await fetch(`${process.env.REACT_APP_URL}/terminal/tcp/disconnect`, { method: "POST" });
    if (!res.ok) throw new Error("Erro ao desconectar");
}

export default function TerminalFireConnection() {
    const classes = useStyles();

    const {openSnackbar} = useSnackbar();

    // Licença Terminal Fire ativa
    const [hasActiveLicense, setHasActiveLicense] = useState(false);

    // Estados
    const [isLoading, setIsLoading] = useState(false);
    const [isConnected, setIsConnected] = useState(false);
    const [open, setOpen] = useState(false);
    const [message, setMessage] = useState("");
    const [severity, setSeverity] = useState("info");

    // IP e Porta
    const [tcpHost, setTcpHost] = useState("");
    const [tcpPort, setTcpPort] = useState("25");
    const [formErrors, setFormErrors] = useState({ host: false, port: false });

    // Lista de IPs sugeridos
    const [suggestedIps, setSuggestedIps] = useState([
        "192.168.0.1",
        "192.168.1.1",
        "10.0.0.1",
        "127.0.0.1"
    ]);

    // Validação de IP simples (IPv4)
    const isValidHost = (host) => {
        const ipRegex = /^(?:(?:25[0-5]|2[0-4]\d|1\d{2}|[1-9]?\d)(?:\.|$)){4}$/;
        return ipRegex.test(host);
    };
    // Validação de porta
    const isValidPort = (port) => {
        const portNumber = parseInt(port, 10);
        return !isNaN(portNumber) && portNumber > 0 && portNumber <= 65535;
    };

    // Carregar dados iniciais
    useEffect(() => {
        async function loadData() {
            setIsLoading(true);
            try {
                const info = await fetchConnectionInfo();
                setTcpHost(info.ip || "");
                setTcpPort(info.port ? String(info.port) : "25");
                setIsConnected(!!info.isConnected);

                const ips = await fetchNetworkIps();
                if (Array.isArray(ips) && ips.length > 0) setSuggestedIps(ips);
            } catch (e) {
                openSnackbar("Erro ao carregar dados do servidor.", "error");
            } finally {
                setIsLoading(false);
            }
        }
        loadData();
    }, []);

    useEffect(() => {
        function check(licenses) {
            setHasActiveLicense(Array.isArray(licenses) && licenses.some(l => l.chave_utilizada));
        }
        onTerminalFireLicenseUpdate(check);
        // Opcional: fetch inicial das licenças se necessário
        return () => window.removeEventListener("terminal-fire-license-update", check);
    }, []);

    // Handler do botão conectar/desconectar
    const handleConnect = async () => {
        const errors = {
            host: !tcpHost || !isValidHost(tcpHost),
            port: !tcpPort || !isValidPort(tcpPort)
        };
        setFormErrors(errors);
        if (Object.values(errors).some(Boolean)) {
            openSnackbar("Preencha os campos corretamente.", "error");
            return;
        }
        setIsLoading(true);
        try {
            if (!isConnected) {
                await connectAndSave(tcpHost, tcpPort);
                setIsConnected(true);
                openSnackbar("Conectado com sucesso!", "success");
            } else {
                await disconnect();
                setIsConnected(false);
                openSnackbar("Desconectado.", "info");
            }
        } catch (e) {
            openSnackbar(e.message || "Erro ao conectar/desconectar.", "error");
        } finally {
            setIsLoading(false);
        }
    };

    // Handler do Snackbar
    const handleSnackBar = () => setOpen(false);

    // Espera para submit futuro
    const handleSubmit = async (e) => {
        e.preventDefault();
        // Aqui pode ser implementado o submit real
    };

    return (
        <>
            <Card elevation={4} className={classes.card} sx={{ position: 'relative', overflow: 'hidden', marginTop: 5 }}>
                {isLoading && (
                    <LinearProgress
                        color="secondary"
                        sx={{
                            width: '100%',
                            position: 'absolute',
                            top: 0,
                            left: 0,
                            borderTopLeftRadius: 1,
                            borderTopRightRadius: 1
                        }}
                    />
                )}
                <CardHeader
                    avatar={<Lan color="primary" />}
                    title="Configuração do Servidor TCP"
                    subheader="Os terminais irão se conectar a este servidor para comunicação"
                />
                <CardContent>
                    {
                        hasActiveLicense ? (
                            <form onSubmit={handleSubmit} autoComplete="off">
                                <Grid container spacing={4}>
                                    <Grid item xs={12} md={6}>
                                        <InputLabel htmlFor="tcp-host" error={formErrors.host}>
                                            Servidor TCP (IP)
                                        </InputLabel>
                                        <TextField
                                            id="tcp-host"
                                            select
                                            value={tcpHost}
                                            error={formErrors.host}
                                            fullWidth
                                            disabled={isLoading || isConnected}
                                            onChange={e => {
                                                setTcpHost(e.target.value.trim());
                                                setFormErrors(prev => ({ ...prev, host: false }));
                                            }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        <Link color={formErrors.host ? "error" : "inherit"} />
                                                    </InputAdornment>
                                                ),
                                            }}
                                            helperText={formErrors.host ? "Formato inválido de IP (ex: 192.168.0.1)" : `O endereço do servidor TCP (ex: 192.168.0.1)`}
                                            FormHelperTextProps={formErrors.host ? { error: true } : {}}
                                        >
                                            {
                                                suggestedIps.map((ip) => (
                                                    <MenuItem key={ip} value={ip}>
                                                        {ip}
                                                    </MenuItem>
                                                ))
                                            }
                                        </TextField>
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <InputLabel htmlFor="tcp-port" error={formErrors.port}>
                                            Porta
                                        </InputLabel>
                                        <TextField
                                            id="tcp-port"
                                            value={tcpPort}
                                            error={formErrors.port}
                                            fullWidth
                                            minLength={1}
                                            maxLength={5}
                                            disabled={isLoading || isConnected}
                                            onChange={e => {
                                                const onlyNumbers = e.target.value.replace(/[^\d]/g, "");
                                                setTcpPort(onlyNumbers);
                                                setFormErrors(prev => ({ ...prev, port: false }));
                                            }}
                                            InputProps={{
                                                startAdornment: (
                                                    <InputAdornment position="start">
                                                        :
                                                    </InputAdornment>
                                                ),
                                            }}
                                            helperText={formErrors.port ? "Porta inválida (1-65535)" : `Porta padrão: 25`}
                                            FormHelperTextProps={formErrors.port ? { error: true } : {}}
                                        />
                                    </Grid>
                                    <Grid item xs={12} justifyContent="flex-end" display="flex">
                                        <Button
                                            color={isConnected ? "error" : "secondary"}
                                            variant="contained"
                                            onClick={handleConnect}
                                            disabled={isLoading}
                                        >
                                            {isConnected ? "Desconectar" : "Conectar"}
                                        </Button>
                                    </Grid>
                                </Grid>
                            </form>
                        ) : (
                            <Alert 
                                severity="warning"
                                variant="outlined"
                                sx={{ 
                                    bgcolor: 'rgba(255, 255, 255, 0.05)',
                                }}
                            >
                                <AlertTitle>Nenhuma licença Terminal Fire ativa encontrada</AlertTitle>
                                Ative uma licença para habilitar a configuração do servidor TCP.
                            </Alert>
                        )
                    }
                </CardContent>
            </Card>
        </>
    );
}